import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';
import { authApi } from '@xtnd/web/features/auth/api/authApi';

/**
 * Mutation for releasing held email for a trust request transaction
 */
export function useReleaseEmailMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ juniperId, emailTemplateOverrideId }: { juniperId: string; emailTemplateOverrideId?: string }) => {
            const currentUser = authApi.getCurrentUser();
            if (!currentUser?.tenantId) {
                throw new Error('Tenant ID not found. Please log in again.');
            }

            const requestId = `trr-release-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            return api.post(
                `/juniper-trr-transactions/${juniperId}/release`,
                {
                    requestId,
                    requestType: 'trr_release',
                    reason: 'Manual email release from trust request transactions page',
                    emailTemplateOverrideId,
                },
                {
                    headers: {
                        'X-Request-Timeout': '60000',
                    },
                }
            );
        },
        onSuccess: (response: any) => {
            if (response.message) {
                showToast.success(response.message);
            } else {
                showToast.success('Email release queued successfully - check back in a few moments...');
            }
            queryClient.invalidateQueries({ queryKey: ['trust-request-transactions'] });
        },
        onError: (error: any) => {
            showToast.error(
                error?.response?.data?.error || error?.message || 'Failed to queue email release. Please try again.'
            );
        },
    });
}

/**
 * Mutation for sending email for a trust request transaction that hasn't been sent yet
 */
export function useSendEmailMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ juniperId }: { juniperId: string }) => {
            const currentUser = authApi.getCurrentUser();
            if (!currentUser?.tenantId) {
                throw new Error('Tenant ID not found. Please log in again.');
            }

            const requestId = `send-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            return api.post(
                `/juniper/transactions/${juniperId}/send`,
                {
                    juniperId,
                    tenantId: currentUser.tenantId,
                    requestId,
                    requestType: 'send',
                    reason: 'Manual email send from trust request transactions page',
                },
                {
                    headers: {
                        'X-Request-Timeout': '60000',
                    },
                }
            );
        },
        onSuccess: (response: any) => {
            if (response.message) {
                showToast.success(response.message);
            } else {
                showToast.success('Email send queued successfully - check back in a few moments...');
            }
            queryClient.invalidateQueries({ queryKey: ['trust-request-transactions'] });
        },
        onError: (error: any) => {
            showToast.error(
                error?.response?.data?.error || error?.message || 'Failed to queue email send. Please try again.'
            );
        },
    });
}

/**
 * Mutation for sending follow-up email for a trust request transaction
 */
export function useResendEmailMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ juniperId, emailTemplateOverrideId }: { juniperId: string; emailTemplateOverrideId?: string }) => {
            const currentUser = authApi.getCurrentUser();
            if (!currentUser?.tenantId) {
                throw new Error('Tenant ID not found. Please log in again.');
            }

            const requestId = `follow-up-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            return api.post(
                `/juniper-trr-transactions/${juniperId}/send-follow-up`,
                {
                    requestId,
                    requestType: 'trr_follow_up',
                    reason: 'Manual email follow-up from trust request transactions page',
                    emailTemplateOverrideId,
                },
                {
                    headers: {
                        'X-Request-Timeout': '60000',
                    },
                }
            );
        },
        onSuccess: (response: any) => {
            if (response.message) {
                showToast.success(response.message);
            } else {
                showToast.success('Email follow-up queued successfully - check back in a few moments...');
            }
            queryClient.invalidateQueries({ queryKey: ['trust-request-transactions'] });
        },
        onError: (error: any) => {
            showToast.error(
                error?.response?.data?.error || error?.message || 'Failed to queue email follow-up. Please try again.'
            );
        },
    });
}

/**
 * Mutation for regenerating PDF for a trust request transaction
 */
export function useRegeneratePdfMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ juniperId }: { juniperId: string }) => {
            const currentUser = authApi.getCurrentUser();
            if (!currentUser?.tenantId) {
                throw new Error('Tenant ID not found. Please log in again.');
            }

            const requestId = `regen-${juniperId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            return api.post<{ downloadUrl: string; filename: string }>(
                `/juniper-trr-transactions/${juniperId}/regenerate-pdf`,
                {
                    requestId,
                },
                {
                    headers: {
                        'X-Request-Timeout': '60000',
                    },
                }
            );
        },
        onSuccess: (response: { downloadUrl: string; filename: string }) => {
            if (response.downloadUrl && !response.downloadUrl.includes('regeneration in progress')) {
                window.open(response.downloadUrl, '_blank');
                showToast.success('PDF regenerated successfully');
            } else {
                showToast.info('PDF regeneration started - check back later');
            }
            queryClient.invalidateQueries({ queryKey: ['trust-request-transactions'] });
        },
        onError: (error: any) => {
            showToast.error(
                error?.response?.data?.error || error?.message || 'Failed to regenerate PDF. Please try again.'
            );
        },
    });
}

/**
 * Mutation for marking a trust request transaction as completed without sending email
 */
export function useMarkCompletedMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ juniperId }: { juniperId: string }) => {
            const requestId = `trr-mark-completed-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            return api.post(
                `/juniper-trr-transactions/${juniperId}/mark-completed`,
                {
                    requestId,
                    reason: 'Manual mark completed from trust request transactions page',
                }
            );
        },
        onSuccess: (response: any) => {
            showToast.success(response?.message || 'Transaction marked as completed');
            queryClient.invalidateQueries({ queryKey: ['trust-request-transactions'] });
        },
        onError: (error: any) => {
            showToast.error(
                error?.response?.data?.error || error?.message || 'Failed to mark as completed. Please try again.'
            );
        },
    });
}

